import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getPublicGuests } from '@/services/guests.api';
import { FrontendGuest } from '@/services/transform';
import GuestDetailModal from './components/GuestDetailModal';
import ResearchFieldCloud from './components/ResearchFieldCloud';
import StructuredData from '@/components/feature/StructuredData';

const HERO_BG = '/images/hero-bg-guests.jpg';

const EXPERTISE_POOL_ZH = ['全部', '人工智能', '量子计算', '材料科学', '生物信息学', '清洁能源', '机器人', '区块链', '神经科学', '气候科学', '纳米技术', '合成生物学', '数字经济'];
const EXPERTISE_POOL_EN = ['All', 'Artificial Intelligence', 'Quantum Computing', 'Materials Science', 'Bioinformatics', 'Clean Energy', 'Robotics', 'Blockchain', 'Neuroscience', 'Climate Science', 'Nanotechnology', 'Synthetic Biology', 'Digital Economy'];

function GuestCard({ guest, isZh, onSelect }: { guest: FrontendGuest; isZh: boolean; onSelect: (g: FrontendGuest) => void }) {
  const { t } = useTranslation();

  return (
    <div
      className="group bg-white rounded-2xl overflow-hidden hover:-translate-y-1.5 transition-all duration-300 cursor-pointer border border-[#0A2540]/8"
      onClick={() => onSelect(guest)}
    >
      <div className="relative overflow-hidden">
        <div className="w-full h-52 bg-gradient-to-br from-[#0A2540] via-[#1a3a5c] to-[#2C7DA0] flex items-center justify-center">
          <img
            src={guest.photo}
            alt={isZh ? guest.name : guest.nameEn}
            className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/80 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
            <i className="ri-calendar-line text-[10px]" />
            <span>{guest.year}</span>
          </div>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-bold text-[#0A2540] text-base mb-0.5 leading-tight">
          {isZh ? guest.name : guest.nameEn}
        </h3>
        <p className="text-[#0A2540]/50 text-xs mb-1">{isZh ? guest.title : guest.titleEn}</p>
        <p className="text-[#2C7DA0] text-xs font-medium mb-3">{isZh ? guest.institution : guest.institutionEn}</p>
        <div className="flex flex-wrap gap-1 mb-4">
          {(isZh ? guest.expertise : guest.expertiseEn).slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-[#F7F9FC] text-[#0A2540]/50 rounded-full text-[10px]">
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1 text-[#2C7DA0] text-xs font-semibold group-hover:gap-2 transition-all">
          <span>{t('guestsPage.viewProfile')}</span>
          <i className="ri-arrow-right-line" />
        </div>
      </div>
    </div>
  );
}

export default function GuestsPage() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [selectedExpertise, setSelectedExpertise] = useState<string>('all');
  const [selectedCountry, setSelectedCountry] = useState<string>('all');
  const [selectedGuest, setSelectedGuest] = useState<FrontendGuest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Load guests from API
  const [guestList, setGuestList] = useState<FrontendGuest[]>([]);

  useEffect(() => {
    getPublicGuests()
      .then(guests => {
        // Deduplicate guests by ID to prevent React key warnings
        const seen = new Set<string>();
        const uniqueGuests = guests.filter((g) => {
          if (seen.has(g.id)) return false;
          seen.add(g.id);
          return true;
        });
        setGuestList(uniqueGuests);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load guests:', err);
        setError(isZh ? '加载嘉宾数据失败' : 'Failed to load guests');
        setLoading(false);
      });
  }, []);

  // Build Schema.org Person structured data
  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'GTRA Distinguished Guest Speakers',
    description: 'Global Technology Roundtable Alliance - featured guest speakers, scholars, scientists and innovation leaders',
    itemListElement: guestList.map((g, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'Person',
        name: g.nameEn,
        jobTitle: g.titleEn,
        affiliation: {
          '@type': 'Organization',
          name: g.institutionEn,
        },
        knowsAbout: g.expertiseEn,
        description: g.bioEn,
        ...(g.publications ? { numberOfItems: g.publications } : {}),
      },
    })),
  }), []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const years = ['all', ...Array.from(new Set(guestList.map((g) => String(g.year)))).sort((a, b) => Number(b) - Number(a))];

  // Build country options from data
  const countryOptions = useMemo(() => {
    const zhList = Array.from(new Set(guestList.map((g) => g.country))).sort();
    const enList = Array.from(new Set(guestList.map((g) => g.countryEn))).sort();
    return { zh: ['全部国家', ...zhList], en: ['All Countries', ...enList] };
  }, []);

  // Deduplicated filtered guests to ensure unique IDs
  const filteredGuests = useMemo(() => {
    const seen = new Set<string>();
    const filtered = guestList.filter((g) => {
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const nameMatch = g.name.toLowerCase().includes(q) || g.nameEn.toLowerCase().includes(q);
        const instMatch = g.institution.toLowerCase().includes(q) || g.institutionEn.toLowerCase().includes(q);
        const titleMatch = g.title.toLowerCase().includes(q) || g.titleEn.toLowerCase().includes(q);
        if (!nameMatch && !instMatch && !titleMatch) return false;
      }
      if (selectedYear !== 'all' && g.year !== Number(selectedYear)) return false;
      if (selectedExpertise !== 'all') {
        const expertiseZh = g.expertise.join('|');
        const expertiseEn = g.expertiseEn.join('|').toLowerCase();
        const match = expertiseZh.includes(selectedExpertise) || expertiseEn.includes(selectedExpertise.toLowerCase());
        if (!match) return false;
      }
      if (selectedCountry !== 'all') {
        const match = isZh
          ? g.country === selectedCountry
          : g.countryEn === selectedCountry;
        if (!match) return false;
      }
      return true;
    });
    
    // Deduplicate by ID
    return filtered.filter((g) => {
      if (seen.has(g.id)) return false;
      seen.add(g.id);
      return true;
    });
  }, [guestList, searchQuery, selectedYear, selectedExpertise, selectedCountry, isZh]);

  const countryList = isZh ? countryOptions.zh : countryOptions.en;

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />
      <StructuredData data={structuredData} id="guests-list" />

      {/* Hero */}
      <section className="relative h-64 md:h-80 flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-cover bg-center bg-top" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 to-[#0A2540]/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/20 text-[#7EC8E3] text-xs font-semibold mb-4 tracking-widest uppercase border border-[#2C7DA0]/30">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-star-line" />
            </div>
            {t('guestsPage.hero.badge')}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
            {t('guestsPage.hero.title')}
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl">
            {t('guestsPage.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Filters & Grid */}
      <section className="py-12 md:py-16" ref={ref}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">

          {/* Research Field Tag Cloud */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <ResearchFieldCloud
              guests={guestList}
              selectedExpertise={selectedExpertise}
              onSelect={setSelectedExpertise}
            />
          </div>

          {/* Search Box */}
          <div className={`mb-4 transition-all duration-700 delay-75 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#0A2540]/35">
                <i className="ri-search-line text-sm" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isZh ? '搜索嘉宾姓名、所属机构或职务关键词…' : 'Search by name, institution or title…'}
                className="w-full pl-10 pr-10 py-3 bg-white border border-[#0A2540]/10 rounded-xl text-sm text-[#0A2540] focus:outline-none focus:border-[#2C7DA0] transition-colors placeholder-[#0A2540]/35"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-[#0A2540]/8 hover:bg-[#0A2540]/15 text-[#0A2540]/50 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xs" />
                </button>
              )}
            </div>
          </div>

          {/* Year & Country Filter Bar */}
          <div className={`bg-white rounded-2xl p-5 mb-8 flex flex-col gap-4 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Year Filter */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[#0A2540]/40 text-xs font-medium whitespace-nowrap">
                  {t('guestsPage.filter.year')}:
                </span>
                {years.map((year) => (
                  <button
                    key={String(year)}
                    onClick={() => setSelectedYear(year as number | 'all')}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      selectedYear === year
                        ? 'bg-[#0A2540] text-white'
                        : 'bg-[#F7F9FC] text-[#0A2540]/60 hover:bg-[#0A2540]/10'
                    }`}
                  >
                    {year === 'all' ? t('guestsPage.year.all') : String(year)}
                  </button>
                ))}
              </div>

              <div className="ml-auto text-[#0A2540]/40 text-xs whitespace-nowrap hidden sm:block">
                {filteredGuests.length} {t('guestsPage.count')}
                {searchQuery.trim() && (
                  <span className="ml-1 text-[#2C7DA0]">
                    {isZh ? `（含搜索"${searchQuery.trim()}"）` : ` (search: "${searchQuery.trim()}")`}
                  </span>
                )}
              </div>
            </div>

            {/* Country Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[#0A2540]/40 text-xs font-medium whitespace-nowrap">
                {t('guestsPage.filter.country')}:
              </span>
              {countryList.map((country) => {
                const isAllTag = country === '全部国家' || country === 'All Countries';
                const isActive = isAllTag ? selectedCountry === 'all' : selectedCountry === country;
                return (
                  <button
                    key={country}
                    onClick={() => setSelectedCountry(isAllTag ? 'all' : country)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-[#1B4F72] text-white'
                        : 'bg-[#F7F9FC] text-[#0A2540]/60 hover:bg-[#1B4F72]/10'
                    }`}
                  >
                    {country}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Guest Grid */}
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            {loading ? (
              // Loading state
              Array.from({ length: 8 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="w-full h-52 bg-gray-200" />
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))
            ) : error ? (
              // Error state
              <div className="col-span-full text-center py-20">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-red-400">
                  <i className="ri-error-warning-line text-3xl" />
                </div>
                <p className="text-sm text-red-500 mb-2">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-1.5 rounded-full bg-[#0A2540] text-white text-xs hover:bg-[#0A2540]/90 transition-colors"
                >
                  {isZh ? '重新加载' : 'Reload'}
                </button>
              </div>
            ) : (
              filteredGuests.map((guest) => (
                <GuestCard
                  key={guest.id}
                  guest={guest}
                  isZh={isZh}
                  onSelect={setSelectedGuest}
                />
              ))
            )}
            {filteredGuests.length === 0 && !loading && !error && (
              <div className="col-span-full text-center py-20 text-[#0A2540]/40">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-user-search-line text-3xl" />
                </div>
                <p className="text-sm">
                  {searchQuery.trim()
                    ? (isZh ? `未找到包含"${searchQuery.trim()}"的嘉宾` : `No guests found for "${searchQuery.trim()}"`)
                    : (isZh ? '暂无符合条件的嘉宾' : 'No guests found matching your criteria')
                  }
                </p>
                {(searchQuery.trim() || selectedYear !== 'all' || selectedExpertise !== 'all' || selectedCountry !== 'all') && (
                  <button
                    onClick={() => { setSearchQuery(''); setSelectedYear('all'); setSelectedExpertise('all'); setSelectedCountry('all'); }}
                    className="mt-3 px-4 py-1.5 rounded-full bg-[#0A2540]/8 text-[#0A2540]/60 text-xs hover:bg-[#0A2540]/15 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {isZh ? '清除所有筛选' : 'Clear all filters'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />

      {selectedGuest && (
        <GuestDetailModal
          guest={selectedGuest}
          onClose={() => setSelectedGuest(null)}
          allGuests={guestList}
          onSelectGuest={(g) => setSelectedGuest(g)}
        />
      )}
    </div>
  );
}
